import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { auth, ApiError } from '../lib/apiClient';
import { passwordPasses } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { PasswordField } from '../components/auth/PasswordField';

export function ResetPasswordPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setCandidate } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = !submitting && token && passwordPasses(password) && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const me = await auth.resetPassword({ token, password });
      // Server auto-logs us in on successful reset.
      setCandidate(me);
      navigate('/');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not reset your password. The link may have expired — request a new one.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthCard publicTheme eyebrow="Reset password" title="Link missing">
        <p className="text-brand-muted mb-6">
          This page expects a reset token in the URL. Please use the link from your email or request a
          new one.
        </p>
        <Link
          to="/forgot-password"
          className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
        >
          Request new reset link
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      publicTheme
      eyebrow="Choose a new password"
      title="Set your new password"
      subtitle="After saving, you&apos;ll be signed in automatically and all other sessions will be ended."
      footer={
        <>
          Don&apos;t need to reset?{' '}
          <Link to="/signin" className="text-brand-primary font-bold hover:text-brand-accent transition-colors">
            Sign in
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <PasswordField
          value={password}
          onChange={setPassword}
          showPolicy
          autoComplete="new-password"
          placeholder="New password"
          label="New password"
        />
        <PasswordField
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          placeholder="Confirm new password"
          label="Confirm new password"
        />
        {confirm && !passwordsMatch && (
          <p className="text-xs text-red-700 font-medium -mt-2">Passwords don&apos;t match.</p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving…' : 'Save new password'}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
    </AuthCard>
  );
}
